// Продление назначения: новый последний день либо «без даты окончания». Близнец закрытия, но
// с противоположным смыслом и противоположными последствиями.
//
// ## Зачем
//
// 10.10.2026 заказчик понял дату «По» в мастере постановки как «период работы» и закрыл людям
// назначения сентябрём. Замер боя 19.10.2026: тридцать человек «на вахте» без действующего
// назначения — их нет ни в табеле, ни в ведомости октября. Продлить закрытое было нечем, а
// «поставить заново» стоило семи полей на человека, разорванной истории тарифа и потерянной
// «первой вахты» вместе с правом на компенсацию билета.
//
// ## Два нажатия
//
// «Продлить» в строке назначения → «Продлить» здесь. Поле «По» по умолчанию ПУСТОЕ: так
// назначение становится без даты окончания, и это единственный вариант, который можно отыграть
// («Закрыть» поставит последний день заново). Продление до даты сократить потом нечем: закрытие
// не трогает назначение с последним днём. Поэтому пустое — умолчание, а дата — осознанный выбор,
// и под полем сказано, с какого дня человека не будет в табеле. У уволенного умолчание другое:
// день увольнения, дальше которого продлевать нельзя.
//
// ## Другой пост — спросить ДО записи
//
// Если в добавляемые дни человек уже стоит на другом посту (обычно это перевод), сервер не
// продлевает, а отвечает отказом с днём и местом. Здесь он показан с второй кнопкой «Продлить всё
// равно» — для того, кто правда работает на двух постах. До 19.10.2026 продление записывалось
// сразу, а замечание приходило после, и отыграть его было нечем.
//
// ## Чего здесь нет
//
// Основания: оно необязательно, и без него сервер пишет в журнал «продление до дд.мм.гггг» —
// третье поле ради редкого случая удлинило бы путь для всех. Тарифа и поста: продление меняет
// ровно последний день, тариф и история тарифа остаются прежними.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiCheckboxCircleLine, RiErrorWarningLine } from '@remixicon/react';

import type { AssignmentOut } from '@/shared/api/endpoints/people';
import { ЗАНЯТ_НА_ДРУГОМ_ПОСТУ } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { ApiError } from '@/shared/errors/api-error';
import {
  ДАТА_ПО_НЕДОПИСАНА,
  вСозданнуюВедомость,
  давноКончилось,
  датаПоНедописана,
  последствиеДатыПо,
  разрывБольшеМесяца,
} from '@/shared/people/последний-день';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly, formatDayAfter } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';

import { WarningsNotice } from '../components/AssignmentResult';
import { useAssignmentInOpenCard, useExtendAssignmentMutation } from '../queries';
import { почемуНельзяПродлить, пределУвольнения } from '../продление';

const EDIT_PERMISSION = 'core.employee.edit';
const ПОЛЕ_ПО = 'extend-assignment-date-to';

const ПУСТО_ЗНАЧИТ =
  'Пустое поле значит «без даты окончания»: человек будет на посту, пока назначение не закроют кнопкой «Закрыть». Так продление можно отыграть.';

export function ExtendAssignmentPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);
  const { назначение, сотрудник } = useAssignmentInOpenCard(id);
  const нельзя = почемуНельзяПродлить(назначение, сотрудник);
  const предел = пределУвольнения(назначение, сотрудник);

  const [датаПо, setДатаПо] = useState(() => предел ?? '');
  // Карточка могла доехать позже первой отрисовки: день увольнения подставляем один раз, когда
  // стало известно, кто это, — и только если человек ещё ничего не набрал.
  const [карточкаУчтена, setКарточкаУчтена] = useState(
    назначение !== undefined && сотрудник !== undefined,
  );
  // Что набрано в поле, пока дата не дописана. Поле отдаёт наружу пустую строку и на
  // недонабранную дату, а пустое здесь значит «без даты окончания» — то есть «31.10.20» молча
  // превратилось бы в бессрочное продление. Набранное держим рядом, чтобы это различить.
  const [набрано, setНабрано] = useState('');
  const [gateNote, setGateNote] = useState<string | null>(null);
  const [итог, setИтог] = useState<AssignmentOut | null>(null);
  const mutation = useExtendAssignmentMutation();

  if (!карточкаУчтена && назначение !== undefined && сотрудник !== undefined) {
    setКарточкаУчтена(true);
    if (предел !== null && датаПо === '' && набрано === '') {
      setДатаПо(предел);
    }
  }

  const занятНаДругомПосту =
    ApiError.is(mutation.error) && mutation.error.code === ЗАНЯТ_НА_ДРУГОМ_ПОСТУ;

  /** Правка поля снимает прежний ответ: он был про другую дату, и «Продлить всё равно» тоже. */
  function правка() {
    setGateNote(null);
    if (!mutation.isPending) {
      mutation.reset();
    }
  }

  function продлить(подтвердитьДваПоста: boolean) {
    if (!allowed) {
      setGateNote(
        'Продлевать назначения может тот, у кого есть право на правку карточек: кадры, финансовый отдел, администратор. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Назначение уже продлевается, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (датаПоНедописана(датаПо, набрано)) {
      setGateNote(ДАТА_ПО_НЕДОПИСАНА);
      return;
    }
    const новое = датаПо === '' ? null : датаПо;
    // Тот же день, что уже стоит: сервер принял бы молча и ничего не изменил, а подтверждение
    // «Назначение продлено» соврало бы. Говорим это здесь и на сервер не ходим.
    if (назначение !== undefined && новое === назначение.date_to) {
      setGateNote(
        новое === null
          ? 'Ничего не изменилось: назначение и так без даты окончания.'
          : `Ничего не изменилось: назначение и так кончается ${formatDateOnly(новое)}. Укажите день позже или оставьте поле пустым.`,
      );
      return;
    }
    setGateNote(null);
    const было = назначение?.date_to;
    mutation.mutate(
      {
        assignmentId: id,
        body: подтвердитьДваПоста
          ? { date_to: новое, confirm_busy_elsewhere: true }
          : { date_to: новое },
        было,
      },
      {
        onSuccess: (ответ) => {
          // Замечание — не отказ: продлено уже. Но закрыть панель молча значило бы спрятать
          // его, а оно ради того и пришло.
          if ((ответ.warnings ?? []).length > 0) {
            setИтог(ответ);
            return;
          }
          onClose();
        },
      },
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    продлить(false);
  }

  if (итог !== null) {
    return (
      <div className='flex flex-col gap-3'>
        <p className='flex items-start gap-1.5 text-paragraph-sm text-text-strong-950'>
          <RiCheckboxCircleLine className='mt-0.5 size-4 shrink-0 text-success-base' aria-hidden />
          <span>
            Назначение продлено:{' '}
            {итог.date_to === null
              ? 'теперь без даты окончания'
              : `теперь последний день ${formatDateOnly(итог.date_to)}`}
            .
          </span>
        </p>
        <WarningsNotice warnings={итог.warnings ?? []} сделано='Назначение уже продлено' />
        <div className='pt-1'>
          <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={onClose}>
            Готово
          </Button.Root>
        </div>
      </div>
    );
  }

  // Продлить нельзя наверняка: на закрытом объекте, снятом посту, уволенному некуда. Формы
  // нет — она вела бы в гарантированный отказ; причина и шаг сказаны сразу.
  if (нельзя !== null) {
    return (
      <div className='flex flex-col gap-3'>
        {назначение !== undefined && <ЧтоПродлеваем назначение={назначение} />}
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>{нельзя}</span>
        </Alert.Root>
        <div className='pt-1'>
          <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
            Закрыть
          </Button.Root>
        </div>
      </div>
    );
  }

  const последствие = последствиеДатыПо(датаПо);
  const конец = назначение?.date_to ?? null;
  const давно = конец !== null && разрывБольшеМесяца(конец, todayIso());

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: назначение не продлится. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      {назначение !== undefined && <ЧтоПродлеваем назначение={назначение} />}

      <p className='text-paragraph-sm text-text-sub-600'>
        {чтоБудет(назначение)} {вСозданнуюВедомость('человек')}
      </p>

      {/* Разрыв больше месяца: продление вернёт человека во ВСЕ табели с того дня, а уезжавшему
          нужна новая постановка. Говорится до поля, а не после нажатия. */}
      {конец !== null && давно && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>{давноКончилось(конец)}</span>
        </Alert.Root>
      )}

      {/* Обёртка ловит набор в поле: поле само отдаёт наружу только целую дату. */}
      <div onInput={(event) => setНабрано((event.target as HTMLInputElement).value)}>
        <DateField
          id={ПОЛЕ_ПО}
          label='По'
          value={датаПо}
          onChange={(значение) => {
            setДатаПо(значение);
            правка();
          }}
          hint={подсказкаПоля(последствие, предел)}
        />
      </div>

      {mutation.isError && (
        <ErrorAlert
          error={mutation.error}
          action={
            занятНаДругомПосту ? (
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => продлить(true)}
              >
                Продлить всё равно
              </Button.Root>
            ) : undefined
          }
        />
      )}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Продлеваем…' : 'Продлить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}

/** «Заря · КПП, сейчас по 30.09.2026» — что именно продлевается. */
function ЧтоПродлеваем({ назначение }: { назначение: AssignmentOut }) {
  return (
    <p className='text-label-sm text-text-strong-950'>
      {назначение.site_name} · {назначение.post_name}
      {назначение.date_to !== null && (
        <span className='text-paragraph-sm text-text-sub-600'>
          , сейчас по {formatDateOnly(назначение.date_to)}
        </span>
      )}
    </p>
  );
}

/** Под полем «По»: последствие даты, а у уволенного — ещё и предел. */
function подсказкаПоля(последствие: string | null, предел: string | null): string {
  if (предел !== null) {
    const граница = `Человек уволен ${formatDateOnly(предел)}: продлить можно не дальше этого дня.`;
    return последствие === null ? граница : `${последствие} ${граница}`;
  }
  return последствие === null
    ? ПУСТО_ЗНАЧИТ
    : `${последствие} Сдвинуть этот день раньше потом будет нечем: если не уверены, оставьте поле пустым.`;
}

/**
 * Одна фраза о последствии: с какого дня человек вернётся в табель.
 *
 * Дата есть, только когда панель открыли из карточки: назначения там уже загружены. Открыли
 * ссылкой — те же слова без даты, а не ожидание отдельного запроса ради одной фразы. Про
 * ведомость здесь не обещается ничего: уже созданная его сама не возьмёт, и это сказано рядом.
 */
function чтоБудет(назначение: AssignmentOut | undefined): string {
  const конец = назначение?.date_to ?? null;
  if (конец === null) {
    return 'Человек вернётся в табель этого поста со дня после прежнего последнего, по тому же тарифу; второго назначения не появится.';
  }
  if (конец < todayIso()) {
    return `С ${formatDayAfter(конец)} человек снова будет в табеле этого поста, по тому же тарифу; второго назначения не появится.`;
  }
  return `Человек останется в табеле этого поста и после ${formatDateOnly(конец)}, по тому же тарифу; второго назначения не появится.`;
}
